import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Check, LocateFixed, Loader2, Search, X } from 'lucide-react';
import { loadCityIndex } from '../../data/worldCities';
import { cityLabel, nearestCity, searchCities, type City, type CityIndex } from '../../utils/cityIndex';
import type { OnboardingCity } from '../../utils/onboardingFlow';

/**
 * ⭐⭐ THE CITY, ASKED ONCE AT THE START (owner, 2026-09-18). Same index as
 * Settings → Location — 34k cities, each carrying its own IANA time zone, which
 * is what the day clock and the prayer layer need. Nothing leaves the device.
 *
 * ⚠️ The chunk is ~710 KB, so it loads when this step renders, not with the app.
 * The field stays usable meanwhile (typing just waits for the same promise).
 */
export function CityField({ value, onChange, fieldStyle }: {
  value: OnboardingCity | null;
  onChange: (city: OnboardingCity | null) => void;
  fieldStyle: CSSProperties;
}) {
  const [index, setIndex] = useState<CityIndex | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [locating, setLocating] = useState(false);
  const [status, setStatus] = useState('');
  const [geoBlocked, setGeoBlocked] = useState(!navigator.geolocation);

  const load = async (): Promise<CityIndex | null> => {
    if (index) return index;
    try {
      const loaded = await loadCityIndex();
      setIndex(loaded);
      setFailed(false);
      return loaded;
    } catch {
      setFailed(true);
      return null;
    }
  };

  // ⚠️ Once, on mount: `load` memoises through `index`, so depending on it would
  // re-run this effect the moment the chunk resolves.
  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const matches = useMemo(
    () => (index && query.trim().length >= 2 ? searchCities(index, query, 8) : []),
    [index, query],
  );

  const pick = (c: City) => {
    onChange({ label: cityLabel(c), lat: c.lat, lng: c.lng, timeZone: c.timeZone, country: c.country });
    setQuery('');
    setStatus('');
  };

  /** Their position, named — the nearest city gives the label AND the time zone. */
  const useMyLocation = () => {
    if (!navigator.geolocation) { setGeoBlocked(true); return; }
    setLocating(true);
    setStatus('Finding you…');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const idx = await load();
        const near = idx ? nearestCity(idx, pos.coords.latitude, pos.coords.longitude) : null;
        setLocating(false);
        if (!near) { setStatus('Could not name that spot — search for your city instead.'); return; }
        // ⚠️ THEIR coordinates, the CITY's zone and name.
        onChange({
          label: near.km <= 15 ? cityLabel(near.city) : `Near ${cityLabel(near.city)}`,
          lat: pos.coords.latitude, lng: pos.coords.longitude,
          timeZone: near.city.timeZone, country: near.city.country,
        });
        setStatus('');
      },
      () => { setLocating(false); setGeoBlocked(true); setStatus('Location is blocked — search for your city.'); },
      { timeout: 8000 },
    );
  };

  if (value) {
    return (
      <div
        className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 lg:px-[18px]"
        style={{ ...fieldStyle, border: '1px solid rgba(60,194,221,.45)' }}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Check className="h-4 w-4 shrink-0" style={{ color: '#3CC2DD' }} />
          <span className="min-w-0">
            <span className="block truncate text-[15px]">{value.label}</span>
            <span className="block truncate text-[12px] opacity-60">{value.timeZone}</span>
          </span>
        </span>
        <button
          type="button"
          onClick={() => { onChange(null); setStatus(''); }}
          aria-label="Change city"
          className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[12px] opacity-70 transition-opacity hover:opacity-100"
        >
          <X className="h-3.5 w-3.5" /> Change
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50 lg:left-[18px]" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your city — Dammam, Barcelona…"
          aria-label="Search your city"
          className="h-[52px] w-full rounded-xl pl-11 pr-4 text-[16px] outline-none lg:pl-12 lg:pr-[18px]"
          style={fieldStyle}
        />
      </div>

      {query.trim().length >= 2 && (
        <div className="overflow-hidden rounded-xl" style={{ border: '1px solid rgba(255,255,255,.11)' }}>
          {!index && !failed ? (
            <p className="flex items-center gap-2 px-4 py-3 text-[13px] opacity-70">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading cities…
            </p>
          ) : failed ? (
            <p className="px-4 py-3 text-[13px] opacity-70">
              The city list could not be loaded. Check your connection and try again.
            </p>
          ) : matches.length === 0 ? (
            <p className="px-4 py-3 text-[13px] opacity-70">No city matches “{query.trim()}”.</p>
          ) : (
            <ul className="max-h-[220px] overflow-y-auto">
              {matches.map((c) => (
                <li key={`${c.name}-${c.country}-${c.lat}-${c.lng}`}>
                  <button
                    type="button"
                    onClick={() => pick(c)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors hover:bg-white/5"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[15px]">{cityLabel(c)}</span>
                      <span className="block truncate text-[12px] opacity-55">{c.timeZone}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!geoBlocked && (
        <button
          type="button"
          onClick={useMyLocation}
          disabled={locating}
          className="inline-flex w-fit items-center gap-2 rounded-lg px-3 py-2 text-[13px] transition-opacity disabled:opacity-50"
          style={{ border: '1px solid rgba(255,255,255,.14)' }}
        >
          <LocateFixed className="h-3.5 w-3.5" /> {locating ? 'Finding you…' : 'Use my location'}
        </button>
      )}
      {status && <p className="m-0 text-[12px] opacity-70">{status}</p>}
    </div>
  );
}
